import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { validateLogin } from '@crossmatch/shared';
import { authApi } from '../api/auth.js';
import type { LoginInput } from '../api/auth.js';
import { isApiError, toUserMessage } from '../api/errors.js';
import { Button } from '../components/Button.js';
import { Card } from '../components/Card.js';
import { VerifyEmail } from '../components/VerifyEmail.js';
import { safeRedirectTarget } from '../lib/navigation.js';
import { useSession } from '../store/session.js';
import { toast } from '../store/toast.js';

/** 登录页：三态 = 初始空表单 / 提交中（按钮转圈）/ 失败（顶部红色提示 + 字段内联错误）。 */
export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{ email?: string; password?: string }>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [phase, setPhase] = useState<'form' | 'verify'>('form');

  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const setSession = useSession((state) => state.setSession);
  const authed = useSession((state) => Boolean(state.accessToken) || Boolean(state.refreshToken));

  const login = useMutation({
    mutationFn: (input: LoginInput) => authApi.login(input),
    onSuccess: (data) => {
      setSession({ user: data.user, accessToken: data.accessToken, refreshToken: data.refreshToken });
      queryClient.clear();
      toast.success(`欢迎回来，${data.user.name}`, '正在进入我的画像');
      navigate(safeRedirectTarget(location.state), { replace: true });
    },
    onError: (error) => {
      // 邮箱未验证：进入内嵌验证视图，验证成功后自动用原账号密码重登。
      if (isApiError(error) && error.code === 'UNAUTHORIZED' && error.message.includes('验证')) {
        setPhase('verify');
        setFormError(null);
        return;
      }
      setFormError(toUserMessage(error));
    },
  });

  if (authed) return <Navigate to="/profile" replace />;

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const parsed = validateLogin({ email, password });
    if (!parsed.ok || !parsed.value) {
      const next: { email?: string; password?: string } = {};
      for (const issue of parsed.issues) {
        if (issue.field === 'email') next.email = issue.message;
        if (issue.field === 'password') next.password = issue.message;
      }
      setFieldErrors(next);
      setFormError('登录信息不完整：请按下方标红提示修正后再提交');
      return;
    }
    setFieldErrors({});
    setFormError(null);
    login.mutate(parsed.value);
  };

  return (
    <div className="auth-shell">
      <Card
        className="auth-card"
        title={phase === 'verify' ? '验证邮箱以继续登录' : '登录 CrossMatch'}
        subtitle={phase === 'verify' ? '该账号邮箱尚未验证，请完成验证后自动登录。' : '松江七校跨校组队引擎：先有画像，才有匹配。'}
      >
        {phase === 'verify' ? (
          <VerifyEmail
            email={email}
            onVerified={() => login.mutate({ email, password })}
            hint={`我们已向 ${email} 发送验证码；若收件箱没有，请检查垃圾邮件，或点「发送验证码」重新获取。`}
          />
        ) : (
        <form className="stack" onSubmit={handleSubmit} noValidate>
          {formError !== null ? (
            <div className="banner banner-danger" role="alert">
              <span className="banner-ico" aria-hidden="true">
                !
              </span>
              <span className="wrap-anywhere">{formError}</span>
            </div>
          ) : null}

          <div className="field">
            <label className="field-label" htmlFor="login-email">
              邮箱
            </label>
            <input
              id="login-email"
              className={fieldErrors.email !== undefined ? 'input input-invalid' : 'input'}
              type="email"
              value={email}
              autoComplete="email"
              placeholder="you@example.com"
              onChange={(event) => setEmail(event.target.value)}
            />
            {fieldErrors.email !== undefined ? <span className="field-error">{fieldErrors.email}</span> : null}
          </div>

          <div className="field">
            <label className="field-label" htmlFor="login-password">
              密码
            </label>
            <input
              id="login-password"
              className={fieldErrors.password !== undefined ? 'input input-invalid' : 'input'}
              type="password"
              value={password}
              autoComplete="current-password"
              placeholder="至少 8 位"
              onChange={(event) => setPassword(event.target.value)}
            />
            {fieldErrors.password !== undefined ? <span className="field-error">{fieldErrors.password}</span> : null}
            <p className="field-hint">
              <Link to="/forgot-password">忘记密码？用邮箱验证码找回</Link>
            </p>
          </div>

          <Button type="submit" variant="primary" className="btn-block" loading={login.isPending}>
            {login.isPending ? '正在登录…' : '登录'}
          </Button>

          <p className="auth-foot">
            还没有账号？<Link to="/register">去注册（只用邮箱，不收学号/手机号）</Link>
          </p>
        </form>
        )}
      </Card>
    </div>
  );
}